import classNames from 'classnames';
import { ClientSelector } from 'components/shared/client-selector';
import { useFileDragDrop } from 'hooks/use-file-drag-drop';
import { useAppSelector } from 'hooks/useAppSelector';
import { ISalesCounterpart } from 'models/ISalesCounterpart';
import { ChangeEvent, RefObject, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import FileCard from 'routes/smart-quote/components/file-card';
import { PromptData } from 'routes/smart-quote/components/prompt-configurator/types';
import { UploadFileOverlay } from 'routes/smart-quote/components/prompt-configurator/upload-file-overlay';
import { trackSmartQuoteEvent } from 'routes/smart-quote/utils';
import { getIsUserInternal } from 'state/selectors/common/user';

type PromptInputProps = {
  onSubmit: (data: PromptData) => void;
  pageRef: RefObject<HTMLDivElement | null>;
};

export const PromptInput = ({ onSubmit, pageRef }: PromptInputProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const promptInputRef = useRef<HTMLDivElement | null>(null);
  const [selectedFile, setFileSelected] = useState<File | null>(null);
  const [selectedClient, setSelectedClient] = useState<ISalesCounterpart | null>(null);
  const isInternalUser = useAppSelector(getIsUserInternal);
  const { formatMessage } = useIntl();

  const { isDragging } = useFileDragDrop(setFileSelected, ['.eml', '.msg']);

  const handleOpenFileExplorer = () => {
    fileInputRef.current?.click();
    trackSmartQuoteEvent('email', 'input bar - add button');
  };

  useEffect(() => {
    if (isDragging) {
      trackSmartQuoteEvent('email', 'drag and drop');
    }
  }, [isDragging]);

  const placeholderMessage = formatMessage({ id: 'smartQuote.workspace.promptConfigurator.promptInput.placeholder' });

  const handleEmailSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    setFileSelected(selectedFile);

    event.target.value = ''; // in order to empty the input and be able to select the same file again if needed
  };

  const handleEmailSubmit = () => {
    if (!selectedFile) {
      return;
    }

    onSubmit({ email: selectedFile, client: selectedClient ?? undefined });
    if (selectedClient) {
      trackSmartQuoteEvent('request quote', 'send button');
    }
    setFileSelected(null);
    setSelectedClient(null);
  };

  const handleSelectClient = (client: ISalesCounterpart | null) => {
    setSelectedClient(client);
    if (client) {
      trackSmartQuoteEvent('select client', 'from input bar');
    }
  };

  return (
    <>
      <div ref={promptInputRef} className={classNames('prompt-input', { 'prompt-input-dragging': isDragging })}>
        {!selectedFile ? (
          <textarea className="form-control form-control-xl bg-transparent bg-blur" placeholder={placeholderMessage} />
        ) : (
          <div className="form-control form-control-xl bg-transparent bg-blur">
            <FileCard
              className="input-mail"
              variant="input"
              fileName={selectedFile.name}
              actionButton={{ onClick: () => setFileSelected(null), iconName: 'close' }}
            />
          </div>
        )}
        <input
          id="fileInput"
          name="fileInput"
          ref={fileInputRef}
          type="file"
          accept=".eml,.msg"
          onChange={handleEmailSelect}
          className="d-none"
        />
        <div className="action-buttons d-flex">
          <div className="btn-group">
            <button
              onClick={handleOpenFileExplorer}
              type="button"
              className="btn btn-flat-primary btn-icon file-input-button"
            >
              <em className="icon icon-md">add</em>
            </button>
            {isInternalUser && (
              <ClientSelector
                onClientChanged={handleSelectClient}
                client={selectedClient}
                containerRef={promptInputRef}
              />
            )}
          </div>
        </div>
        <div className="send-button">
          <button
            className="btn btn-primary btn-icon"
            type="button"
            onClick={handleEmailSubmit}
            disabled={!selectedFile}
          >
            <em className="icon">arrow_forward</em>
          </button>
        </div>
      </div>

      {isDragging && <UploadFileOverlay containerRef={pageRef} />}
    </>
  );
};
